import _, {includes} from 'lodash';
import {Button, Input, Tab, Tabs, Textarea, Tooltip, useDisclosure} from "@heroui/react";
import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useDebouncedCallback, useDebouncedValue, useViewportSize,} from '@mantine/hooks';
import {useForm} from '@mantine/form';
import Header from '../../components/Header.jsx';
import {NumberFormatter} from '@mantine/core';
import repositories from '../../repositories.jsx';
import {useEffect, useMemo, useRef, useState} from 'react';
import {appHelper} from '../../helpers/AppHelper.jsx';
import TextToSpeechSettings from '../../components/textToSpeech/TextToSpeechSettings.jsx';
import History from '../../components/textToSpeech/History.jsx';
import toast from 'react-hot-toast';
import {AudioPlayer} from "react-audio-play";
import {AnimatePresence, motion} from 'framer-motion';
import PageLoader from '../../components/PageLoader.jsx';
import Languages from '../../components/textToSpeech/Languages.jsx';
import {useRecoilState} from 'recoil';
import {userState} from '../../store.jsx';

const inputWrapper = 'min-h-12s !bg-white border-1 data-[focus=true]:!border-default data-[hover=true]:border-default shadow-none';
export default function TextToSpeech({search = null}) {
    const {isOpen, onClose, onOpen, onOpenChange} = useDisclosure()
    const {width, height} = useViewportSize()
    const [isLoading, setIsLoading] = useState(true)
    const [authMe] = useRecoilState(userState)
    const [pauseValue, setPauseValue] = useState(0.5)
    const textareaRef = useRef(null);

    const queryClient = useQueryClient()

    const form = useForm({
        initialValues: {
            search_in_history: '',
            prompt_character: 5000,
            min_rows: 14,
            voice_model: '',
            text: '',
            language: '',
            model: '',
            voice_id: '',
            speed: 1,
            stability: 0,
            style: 0.4,
            use_speaker_boost: false,
            emotion: 'happy',
            current_audio: {},
        }
    })

    const history = useInfiniteQuery({
        queryKey: ['history'],
        queryFn: ({pageParam}) => repositories.textToSpeech.history({page: pageParam, per_page: 10}),
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages, lastPageParam) => (_.size(lastPage?.items) >= lastPage?.per_page) ? lastPageParam + 1 : null,
    })

    const [debouncedSearch] = useDebouncedValue(_.trim(form.values.search_in_history), 500)

    const allHistory = useMemo(() => {
        const mergedItems = _.flatMap(history?.data?.pages, (item) => item?.items || []);

        const audioData = appHelper.filterArrayContent(mergedItems || [], _.trim(debouncedSearch), (item) => item?.text) || []
        return _.orderBy(_.uniqBy(audioData, 'audio_file') || audioData, ['created_at'], ['desc'])
    }, [debouncedSearch, history.data])

    const fetchConfig = useQuery({
        queryKey: ['fetchConfig'],
        queryFn: () => repositories.textToSpeech.config().then(async (res) => {
            //await form.setFieldValue('model', _.find(res?.models, item => item?.provider == 'edge_tts')?.model_id || null)
            //await form.setFieldValue('voice_model', _.find(res?.models, item => item?.provider == 'edge_tts')?.provider || null)
            setIsLoading(false)
            return res || null
        })
    })

    const fetchVoices = useQuery({
        queryKey: ['fetchVoices', form.values.model, form.values.language],
        enabled: !_.isEmpty(form.values.model),
        queryFn: () => repositories.textToSpeech.voices(form.values).then((res) => {
            setIsLoading(false)
            return res || null
        })
    })

    const onGenerateSpeech = useMutation({
        mutationFn: repositories.textToSpeech.generate,
        onMutate: () => {
            onClose()
        },
        onSuccess: async (data) => {
            if (_.isEmpty(data?.audio_file)) {
                toast.error('Speech generated, but audio file is missing');
            } else {
                toast.success('Speech successfully generated')
                form.setFieldValue('current_audio', {
                    name: form.values.text || "music - 1",
                    src: data?.audio_file,
                    is_playing: true
                })
            }
            history.refetch()
            queryClient.refetchQueries({
                queryKey: ['auth'],
            })
        },
        onError: error => {
            toast.error(error?.response?.data?.detail || 'Something went wrong')
        }
    })

    const getAllModels = useMemo(() => {
        const freeModels = _.filter(fetchConfig?.data?.models, item => _.includes(['standard'], _.lowerCase(item?.speaker_type || 'Standard')))
        const filteredModels = _.filter(fetchConfig?.data?.models || [], item => _.includes(item?.languages, form.values?.language))
        const mergeModels = _.concat(_.isEmpty(filteredModels) ? fetchConfig?.data?.models : filteredModels, freeModels)
        return _.isEmpty(mergeModels) ? [] : _.uniqBy(mergeModels, 'model_id')
    }, [fetchConfig?.data?.models, form.values.language])


    const voices = useMemo(() => {
        const voices = appHelper.filterArrayContent(fetchVoices?.data, form.values?.language, item => {
            return `${item?.language}`
        })
        const mergeData = _.isEmpty(voices) ? fetchVoices?.data : voices
        if (_.isEmpty(form.values?.voice_id)) {
            setTimeout(() => form.setFieldValue('voice_id', _.head(mergeData)?.voice_id), 300)
        }
        return mergeData
    }, [fetchVoices?.data])

    const onChangedLanguage = useDebouncedCallback(() => {
        if (!_.isEmpty(getAllModels)) {
            const freeModels = _.filter(fetchConfig?.data?.models, item => _.includes(['standard'], _.lowerCase(item?.speaker_type || 'Standard')))
            let model = _.includes([1, 2], authMe?.current_plan) ? _.head(freeModels) : _.head(getAllModels);
            form.setFieldValue('model', model?.model_id);
            form.setFieldValue('voice_model', model?.provider);
        }
    }, 50);
    const [result, setResult] = useState({
        total: 0,
        chinese: 0,
        japanese: 0,
        other: 0
    });

    const tokens = useMemo(() => {
        const input = form?.values?.text;
        const chineseRegex = /\p{Script=Han}/u; ///[\u4e00-\u9fff]/;         // Chinese characters (Han)
        const japaneseRegex = /[\u3040-\u30ff\u31f0-\u31ff]/; // Japanese (Hiragana, Katakana)

        let chinese = 0;
        let japanese = 0;
        let other = 0;
        let total = 0;

        for (let char of input) {
            if (chineseRegex.test(char)) {
                chinese++;
            } else if (japaneseRegex.test(char)) {
                japanese++;
            } else if (/\s/.test(char)) {
                other++;
            } else {
                other++;
            }
        }
        total = _.size(input)

        setResult({
            total,
            chinese,
            japanese,
            other
        });

        const chineseOrJapanese = chinese + japanese


        return (chineseOrJapanese * 2) + other;
    }, [form?.values?.text])


    useEffect(() => {
        if (authMe?.trial_status === 'active') {
            form.setFieldValue('prompt_character', 5000)
        }
    }, [form.values?.voice_model])


    const onPauseClick = () => {
        if (pauseValue >= 0.1 && form.values.model === "minimax" && textareaRef.current) {
            const pauseTag = `[p-${pauseValue}]`;
            const currentText = form.values.text || "";

            // Get the current cursor position using the ref
            const cursorPosition = textareaRef.current.selectionStart;

            // Insert the pause tag at the cursor position
            const newText = currentText.slice(0, cursorPosition) + pauseTag + currentText.slice(cursorPosition);

            if (tokens <= form.values.prompt_character) {
                form.setFieldValue('text', newText, {shouldValidate: false});

                // Set the cursor position to just after the inserted pause tag
                setTimeout(() => {
                    textareaRef.current.setSelectionRange(cursorPosition + pauseTag.length, cursorPosition + pauseTag.length);
                }, 0);
            }
        }
    }

    const addPauseHandler = (e) => {
        let isMac = false;

        if (navigator.userAgentData) {
            isMac = navigator.userAgentData.platform.toLowerCase().includes('mac');
        } else {
            isMac = navigator.userAgent.toLowerCase().includes('mac');
        }
        const isPauseShortcut =
            (isMac && e.ctrlKey && e.key.toLowerCase() === 'p') ||
            (!isMac && e.altKey && e.key.toLowerCase() === 'p');

        if (isPauseShortcut && form.values.model === "minimax") {
            e.preventDefault();
            onPauseClick()
        }
    }

    return (
        <div className="relative">
            <Header currentPage="text-to-speech"/>
            {/*{form.values.voice_id}*/}
            {/*{JSON.stringify(result)}*/}
            <div
                className="p-5 relative rounded-3xl bg-white border border-slate-200/80 shadow-[0_10px_40px_-28px_rgba(15,23,42,0.35)] flex gap-4">
                <PageLoader isLoading={isLoading}
                            className="absolute w-full h-full bg-white/30 backdrop-blur-sm rounded-3xl"/>
                <div className="grow">
                    <div
                        className="flex flex-wrap justify-between items-center mb-5 gap-3 border-b border-slate-200/60 pb-5">
                        <div className="flex items-center gap-3 mr-auto">
                            <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-gradient-to-br from-primary to-fuchsia-500 text-white shadow-md shadow-fuchsia-500/30">
                                <i className="hgi-stroke hgi-sparkles text-xl"></i>
                            </span>
                            <div>
                                <h2 className="text-lg font-bold text-slate-800 leading-tight tracking-tight">AI Speech Studio</h2>
                                <p className="text-[12.5px] text-slate-500">Turn any text into lifelike speech</p>
                            </div>
                        </div>
                        {includes([2, 3, 5, 7], authMe?.current_plan) &&
                            (
                                <div
                                    className="bg-default-200/30 py-2 px-4 rounded-2xl flex gap-3 relative items-center w-60">
                                    <div className="w-full">
                                        <Input
                                            readOnly={form.values.model !== "minimax"}
                                            value={pauseValue}
                                            onValueChange={(value) => {
                                                setPauseValue(Number(value))
                                            }}
                                            classNames={{
                                                inputWrapper: "!shadow-none h-[42px] px-4 w-full !bg-white focus-within:!border-slate-800 focus-within:!bg-white hover:!bg-white rounded-xl border-2 border-slate-300 focus-within:border-slate-800",
                                                input: "!text-sm !font-light text-gray-400 placeholder:text-gray-400 outline-none pr-[90px]",
                                            }}
                                            step="0.1" max="60" min="0.1" type="number"/>
                                        <Button
                                            onPress={onPauseClick}
                                            disabled={form.values.model !== "minimax"}
                                            className="absolute !rounded-[10px] right-[18px] top-[15px] -translate-y-1.5 ">
                                            Add Pause
                                        </Button>
                                    </div>
                                </div>
                            )
                        }
                        <div className="bg-default-200/30 py-2 px-4 rounded-2xl flex gap-3 items-center w-80">
                            <h3 className="font-medium text-medium text-slate-800">
                                Language
                            </h3>
                            <div className="w-full">
                                <Languages
                                    onChanged={(value) => {
                                        form.setFieldValue('language', value)
                                        form.setFieldValue('voice_id', null)
                                        onChangedLanguage()
                                    }}
                                    form={form}/>
                            </div>
                        </div>
                    </div>
                    <Textarea
                        ref={textareaRef}
                        onKeyDown={(e) => {
                            addPauseHandler(e)
                        }}
                        isDisabled={onGenerateSpeech.isPending}
                        minRows={1} maxRows={form.values.min_rows + 1}
                        size="lg" maxLength={form.values.prompt_character}
                        value={form.values.text}
                        onValueChange={(value) => {
                            // Define regex patterns for different languages
                            const chineseRegex = /\p{Script=Han}/u; // Chinese characters (Han)
                            const japaneseRegex = /[\u3040-\u30ff\u31f0-\u31ff]/; // Japanese (Hiragana, Katakana)

                            // Count the occurrence of each language in the provided content
                            let chineseCount = 0;
                            let japaneseCount = 0;
                            let englishCount = 0;

                            for (let char of value) {
                                if (chineseRegex.test(char)) {
                                    chineseCount++;
                                } else if (japaneseRegex.test(char)) {
                                    japaneseCount++;
                                } else if (/[a-zA-Z]/.test(char)) {
                                    englishCount++;
                                }
                            }
                            if (chineseCount > 0 || japaneseCount > 0) {
                                value = value.slice(0, 2754);
                            }

                            if (tokens <= form.values.prompt_character) {
                                form.setFieldValue('text', value);
                            }
                        }}
                        className="w-full"
                        placeholder={`${includes([2, 3, 5, 7], authMe?.current_plan) ? "Start typing here or paste any text you want to turn into lifelike speech. \n💡 Press ⌃ + P on Mac or Alt + P on Windows to insert a pause.\n💡 You can also manually add a pause using [p-0.5], where 0.5 means a 0.5s pause.\n💡 Pause feature works only with the Audio Dub V2 model."
                            : "Start typing here or paste any text you want to turn into lifelike speech."}`}
                        variant="bordered"
                        classNames={{
                            inputWrapper: '!bg-transparent border-0 border border-slate-200 shadow-none p-4',
                            input: 'overflow-y-auto min-h-[50dvh] max-h-[50dvh] leading-8',
                        }}
                    />
                    <div className="flex justify-between items-center gap-3 mt-8 mb-5">
                        <div className="text-left flex justify-between flex-wrap items-center opacity-60">
							<span className="inline-block">
								<NumberFormatter
                                    suffix="/"
                                    value={tokens}
                                    thousandSeparator
                                />
								<NumberFormatter
                                    suffix=" characters. "
                                    //value={authMe?.available_credit || 0}
                                    value={form.values.prompt_character}
                                    thousandSeparator
                                />
							</span>
                            <div className="ml-1 inline-flex gap-1 items-center">
                                <Tooltip color="foreground" className="max-w-80 text-center" showArrow content="1 credit for each English or Indian
							character and 2 credits for each Chinese or Japanese character">
                                    <i className="hgi-stroke hgi-information-circle font-bold text-primary"></i>
                                </Tooltip>
                                <div>
                                    <strong> {tokens} credits</strong> will
                                    be charged to your account.
                                </div>
                            </div>
                        </div>
                        <div className="text-right flex justify-end gap-3 items-center">
                            {_.size(form.values.text) > 2 &&
                                <Button
                                    color="default"
                                    variant="light"
                                    radius="full"
                                    isDisabled={onGenerateSpeech.isPending}
                                    onPress={() => form.setValues({text: ''})}
                                    startContent={<i className="hgi-stroke hgi-cancel-circle-half-dot text-xl"></i>}
                                >
                                    Clear
                                </Button>
                            }
                            <div>
                                <Button
                                    radius="full"
                                    className="bg-gradient-to-r from-primary to-fuchsia-500 text-white font-semibold px-6 shadow-lg shadow-fuchsia-500/30 hover:scale-[1.02] transition-transform"
                                    isDisabled={_.size(form.values.text) < 1 || _.isEmpty(form.values.language) || _.isEmpty(form.values.voice_id)}
                                    isLoading={onGenerateSpeech.isPending}
                                    startContent={<i className="hgi-stroke hgi-sparkles text-xl"></i>}
                                    onPress={() => {
                                        let speed = 0;

                                        if (form.values.voice_model == 'edge_tts') {
                                            speed = Math.round((form.values.speed === 1 ? 0 : (form.values.speed > 1 ? (form.values.speed - 1) * 100 : (form.values.speed - 1) * 100)));
                                            speed = speed >= 0 ? `+${speed}%` : `${speed}%`;
                                        } else {
                                            speed = form.values.speed;
                                        }

                                        onGenerateSpeech.mutate(_.merge(_.cloneDeep(form.values), {
                                            speed: _.toString(speed),
                                            stability: _.toString(form.values.stability >= 0 ? `+${form.values.stability}` : form.values.stability)
                                        }))
                                    }}
                                >
                                    {onGenerateSpeech?.isSuccess ? 'Generate again' : 'Generate speech'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className="min-w-96 max-w-96 relative bg-default-100 border border-slate-200/80 px-2 py-2 rounded-2xl grid-rows-1 overflow-hidden text-slate-400">
                    <PageLoader isLoading={onGenerateSpeech.isPending || fetchVoices?.isFetching}
                                className="absolute w-full h-full bg-white/5 backdrop-blur-[2px] rounded-3xl"/>
                    <Tabs
                        isDisabled={onGenerateSpeech.isPending}
                        classNames={{
                            panel: 'h-[95%]'
                        }}
                        className="w-full" aria-label="Tabs variants" variant="underlined">
                        <Tab key="settings" title="Settings">
                            {!form.values.language &&
                                <div
                                    className="absolute left-0 bottom-0 w-full h-[90%] xl:h-[92%] bg-default-500/20 backdrop-blur z-20 font-medium text-center px-4 flex flex-col justify-center items-center text-default-600">
                                    <i className="hgi hgi-stroke hgi-alert-02 text-4xl mb-3 text-warning"></i>
                                    <h4>Please choose the language, then access voice settings</h4>
                                </div>
                            }
                            <TextToSpeechSettings
                                form={form}
                                allVoices={voices}
                                config={fetchConfig?.data}
                                models={getAllModels || []}/>
                        </Tab>
                        <Tab key="history" title="History">
                            <History
                                form={form}
                                key={_.size(allHistory)}
                                speakers={allHistory}
                                query={history}
                            />
                        </Tab>
                    </Tabs>
                </div>
            </div>
            <AnimatePresence>
                {!_.isEmpty(form.values?.current_audio) &&
                    <motion.div
                        initial={{
                            y: 200,
                        }}
                        animate={{
                            y: [200, 0],
                        }}
                        exit={{
                            y: [0, 200],
                        }}
                        transition={{
                            duration: 0.5,
                            delay: 0.2,
                            type: 'spring',
                        }}
                        className="fixed bottom-1 left-0 w-full flex justify-center z-50">
                        <Button
                            className="!bg-black text-white absolute rounded-t-2xl -top-2.5 !opacity-100 right-1/2 translate-x-1/2 -translate-y-1/2 z-50"
                            onPress={() => {
                                form.setFieldValue('current_audio', null)
                            }}>
                            <i className="hgi-stroke hgi-cancel-01 text-xl"></i>
                            {/*<i className="hgi-stroke hgi-arrow-up-01 text-xl"></i>*/}
                        </Button>
                        <div className="w-full max-w-[95dvw] flex items-center gap-4 bg-black rounded-2xl p-4 shadow-2xl ring-1 ring-white/10">
                            <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-gradient-to-br from-primary to-fuchsia-500 text-white">
                                <i className="hgi-stroke hgi-sparkles text-lg"></i>
                            </span>
                            <h2 className="truncate text-white min-w-80 max-w-80">{form.values.current_audio.name}</h2>
                            <AudioPlayer
                                key={form.values?.current_audio?.src}
                                src={form.values?.current_audio?.src}
                                hasKeyBindings
                                onPlay={() => form.setFieldValue('current_audio.is_playing', true)}
                                onEnd={() => form.setFieldValue('current_audio.is_playing', false)}
                                onPause={() => form.setFieldValue('current_audio.is_playing', false)}
                                preload width="100%"
                                color="white" backgroundColor="black"
                                autoPlay
                            />
                        </div>
                    </motion.div>
                }
            </AnimatePresence>
        </div>
    )
}
